import { useCallback, useState } from 'react';
import { commitMutation } from 'react-relay';
import type { MutationConfig, MutationParameters, PayloadError } from 'relay-runtime';
import type { DeepPartial } from 'ts-essentials';
import { RelayEnvironment } from './relay-env';

export type FragmentData<TFragment> = DeepPartial<TFragment>;

export type GenericMutation<TOperation extends MutationParameters = MutationParameters> = {
  config: Omit<MutationConfig<TOperation>, 'onCompleted' | 'variables' | 'onError'>;
  variables: TOperation['variables'];
  onCompleted?: (response: TOperation['response'], errors: readonly PayloadError[] | null | undefined) => void;
};

type GenericMutationFnConfig<TOperation extends MutationParameters> = Omit<
  GenericMutation<TOperation>['config'],
  'mutation'
> & {
  onCompleted?: GenericMutation<TOperation>['onCompleted'];
};

export const genericMutation = <TOperation extends MutationParameters = MutationParameters>(
  config:
    | GenericMutation<TOperation>['config']
    | ((variables: TOperation['variables']) => GenericMutation<TOperation>['config'])
) => {
  return (variables: TOperation['variables'], fnConfig?: GenericMutationFnConfig<TOperation>) => {
    const rootConfig = typeof config === 'function' ? config(variables) : config;

    commitMutation<TOperation>(RelayEnvironment, {
      ...rootConfig,
      variables,
      ...fnConfig,
      onError: (error) => {
        fnConfig?.onCompleted?.({}, [error]);
      },
    });
  };
};

export const useGenericMutation = <TOperation extends MutationParameters = MutationParameters>(
  mutation: ReturnType<typeof genericMutation<TOperation>>
) => {
  return () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<TOperation['response'] | null>(null);
    const [errors, setErrors] = useState<readonly PayloadError[] | null | undefined>(null);

    const commit = useCallback(
      (vars: TOperation['variables'], fnConfig?: GenericMutationFnConfig<TOperation>) => {
        if (loading) return;

        setLoading(true);
        mutation(vars, {
          ...fnConfig,
          onCompleted: (data, errors) => {
            setLoading(false);
            setData(data);
            if (!!errors?.length) {
              setErrors(errors);
            }
            fnConfig?.onCompleted?.(data, errors);
          },
        });
      },
      [loading]
    );

    return [commit, loading, data, errors] as const;
  };
};
